import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import type {
    FrageAdressat,
    NeuePlanungsfrage,
    NeuerPlanungshinweis,
    PflichtEintrag,
    Planungsfrage,
    Planungshinweis,
    Planungsphase,
    PlanungStatus,
    ProjektMerkmale,
    SchwellenwertErgebnis,
} from '../components/planungsphase/types';
import {
    aktuelleKatalogVersion,
    fristDatum,
    heuteLokalIso,
    naechsteHinweisNummer,
    pflichtenStand,
    pflichtenUngeklaert,
} from '../components/planungsphase/planungLogic';
import { normalisiereUnterlagen } from '../components/planungsphase/unterlagenLogic';
import { antwortWegAus } from '../components/planungsphase/antwortQuelle';
import {
    EmpfaengerSchreibFehler,
    gleicherEintrag,
    normalisiereEmpfaenger,
    setzeEmpfaenger,
    type EmpfaengerBasis,
    type EmpfaengerEintrag,
    type EmpfaengerSchreibErgebnis,
    type EmpfaengerZuordnung,
} from '../components/planungsphase/empfaenger';
import type { FristErgebnis, FristGruppe } from '../components/planungsphase/fristGruppen';
import { merkmaleFingerabdruck } from '../components/planungsphase/leitfaden';

/**
 * Datenbankzugriff des Moduls "Planungsphase" (Tabellen `planungsphasen`,
 * `planungsfragen`, `planungshinweise` — supabase/planungsphase.sql,
 * MIGRATIONS.md Nr. 39). Die einzige Stelle, die mit ihnen redet.
 *
 * Die REGELN liegen daneben in `src/components/planungsphase/planungLogic.ts`
 * und sind ohne Datenbank pruefbar; hier steht nur, was ohne Supabase nicht
 * zu haben ist.
 *
 * Drei Hausregeln, die hier durchgaengig gelten:
 *
 * (a) LESEN IST FAIL-OPEN, SCHREIBEN NICHT. Fehlt die Tabelle (Migration noch
 *     nicht gefahren), liefert das Lesen eine leere Lage statt zu werfen —
 *     dann gibt es eben noch keine Planungsphase. Jeder schreibende Aufruf
 *     meldet den Fehler dagegen sichtbar; still zu scheitern waere hier
 *     besonders teuer, weil die Akte gerade den Nachweis tragen soll.
 *
 * (b) KEIN LESEN-DANN-SCHREIBEN. Wo eine Bedingung gilt (nur Entwuerfe
 *     aendern, nur einmal versenden), steht sie als `where`-Bedingung IM
 *     UPDATE und nicht als vorheriges `select`. Zwischen Lesen und Schreiben
 *     passt eine zweite Sitzung.
 *
 * (c) DIE LAUFENDE NUMMER WIRD UNMITTELBAR VOR DEM INSERT BESTIMMT und bei
 *     `23505` neu versucht. Sie vorab zu merken und spaeter zu schreiben hat
 *     im Repo schon einmal einen Begehungseintrag ueberschrieben.
 */

const T_PHASE = 'planungsphasen';
const T_FRAGE = 'planungsfragen';
const T_HINWEIS = 'planungshinweise';

/** PostgREST liefert je nach Schema-Cache 42P01 oder PGRST205. */
const errorCode = (e: unknown): string => {
    const c = (e as { code?: unknown })?.code;
    return typeof c === 'string' ? c : '';
};
const istFehlendeTabelle = (e: unknown): boolean => {
    const code = errorCode(e);
    return code === 'PGRST205' || code === '42P01';
};
const istDublette = (e: unknown): boolean => errorCode(e) === '23505';
/** Spalte fehlt (Migration noch nicht gefahren): Postgres beim Lesen 42703,
 *  PostgREST beim Schreiben PGRST204 (Schema-Cache kennt die Spalte nicht). */
const istFehlendeSpalte = (e: unknown): boolean => {
    const code = errorCode(e);
    return code === '42703' || code === 'PGRST204';
};

/** jsonb-Spalte `merkmale`: alles, was kein Objekt ist, zaehlt als „nichts
 *  angegeben" — und nicht als Absturz in der ersten Lesestelle. */
const merkmaleAus = (v: unknown): ProjektMerkmale =>
    (v && typeof v === 'object' && !Array.isArray(v)) ? (v as ProjektMerkmale) : {};

const pruefungAus = (v: unknown): SchwellenwertErgebnis | null =>
    (v && typeof v === 'object') ? (v as SchwellenwertErgebnis) : null;

/** Vorgaben fuer Spalten, die die Datenbank NULL-bar haelt, der Client aber
 *  nie NULL sehen soll — sonst steht im Textfeld "null". */
const mapPhase = (row: Record<string, unknown>): Planungsphase => ({
    ...(row as unknown as Planungsphase),
    baubeschreibung: typeof row.baubeschreibung === 'string' ? row.baubeschreibung : '',
    bauaufgabe: typeof row.bauaufgabe === 'string' ? row.bauaufgabe : '',
    merkmale: merkmaleAus(row.merkmale),
    pruefung: pruefungAus(row.pruefung),
    katalog_version: typeof row.katalog_version === 'number' ? row.katalog_version : 1,
    // Spalte aus Migration Nr. 156. Fehlt sie oder steht Unbrauchbares darin,
    // gibt es eine leere Checkliste statt eines Absturzes (siehe (a)).
    unterlagen: normalisiereUnterlagen(row.unterlagen),
});

const mapFrage = (row: Record<string, unknown>): Planungsfrage => ({
    ...(row as unknown as Planungsfrage),
    adressat: Array.isArray(row.adressat) ? (row.adressat as Planungsfrage['adressat']) : [],
    rechtsgrundlage: Array.isArray(row.rechtsgrundlage) ? (row.rechtsgrundlage as string[]) : [],
    antwort: typeof row.antwort === 'string' ? row.antwort : '',
    bewertung: typeof row.bewertung === 'string' ? row.bewertung : '',
    sortierung: typeof row.sortierung === 'number' ? row.sortierung : 0,
    eigene_frage: row.eigene_frage === true,
    // Quelle der Antwort (Nr. 190). Fehlt die Spalte (Migration noch nicht
    // gefahren) oder steht Unbrauchbares darin: `null` = nicht erfasst.
    antwort_von: typeof row.antwort_von === 'string' ? row.antwort_von : null,
    antwort_weg: antwortWegAus(row.antwort_weg),
    antwort_beleg_id: typeof row.antwort_beleg_id === 'string' && row.antwort_beleg_id ? row.antwort_beleg_id : null,
});

const mapHinweis = (row: Record<string, unknown>): Planungshinweis => ({
    ...(row as unknown as Planungshinweis),
    rechtsgrundlage: Array.isArray(row.rechtsgrundlage) ? (row.rechtsgrundlage as string[]) : [],
    verteiler: Array.isArray(row.verteiler) ? (row.verteiler as string[]) : [],
    frage_codes: Array.isArray(row.frage_codes) ? (row.frage_codes as string[]) : [],
    reaktion: typeof row.reaktion === 'string' ? row.reaktion : '',
    adressat_ist_bauherr: row.adressat_ist_bauherr === true,
});

/**
 * Spalten, die der Client NIE mitschickt. `updated_at` kommt vom Trigger,
 * die Identitaeten und Fremdschluessel stehen fest — ein Update, das sie
 * mitschickt, kann eine Zeile in ein fremdes Projekt umhaengen.
 *
 * `empfaenger` (Nr. 162) hat ihren EIGENEN Schreibweg (`speichereEmpfaenger`,
 * Teil-Update nur dieser Spalte). Sie steht hier, damit ein per
 * `select('*')` gelesener und zurueckgeschriebener Kopfsatz („Grundlagen
 * speichern") eine inzwischen geaenderte Zuordnung nicht still ueberschreibt.
 */
const GESPERRTE_SPALTEN = new Set([
    'id', 'planung_id', 'project_id', 'created_at', 'updated_at', 'nummer', 'empfaenger',
]);

const ohneGesperrteSpalten = <T extends object>(updates: T): Record<string, unknown> => {
    const rein: Record<string, unknown> = {};
    Object.entries(updates as Record<string, unknown>).forEach(([k, v]) => {
        if (GESPERRTE_SPALTEN.has(k)) return;
        // Leerstring bei Datumsspalten heisst "keine Angabe" und muss als
        // NULL ankommen — PostgREST wuerde '' sonst als Datum parsen wollen.
        if (v === '' && (k.endsWith('_am') || k === 'frist_am')) rein[k] = null;
        else rein[k] = v;
    });
    return rein;
};

// ═══════════════════════════════════════════════════════════════════════════
// KOPFSATZ
// ═══════════════════════════════════════════════════════════════════════════

/** Leseart fuer Anzeigen, die aus „keine Phase" etwas SCHLIESSEN (Ampel,
 *  Steckbrief): `streng` wirft auch bei fehlender Tabelle. Sonst saehe ein
 *  nicht gelaufenes Schema wie „noch nicht angelegt" aus — grau statt Fehler. */
export interface PlanungLeseOptionen { streng?: boolean }

/** Die Planungsphase eines Projekts, oder `null`, wenn es noch keine gibt.
 *  Wirft NICHT bei fehlender Tabelle (siehe (a) im Dateikopf), ausser mit
 *  `streng`. */
export const fetchPlanungsphase = async (
    projectId: string,
    optionen: PlanungLeseOptionen = {},
): Promise<Planungsphase | null> => {
    if (!projectId) return null;
    const { data, error } = await supabase
        .from(T_PHASE)
        .select('*')
        .eq('project_id', projectId)
        .maybeSingle();
    if (error) {
        if (istFehlendeTabelle(error) && !optionen.streng) return null;
        if (istFehlendeTabelle(error)) {
            throw new Error('Die Tabellen der Planungsphase fehlen noch (Migration Nr. 39) — Stand nicht lesbar.');
        }
        throw new Error(describeError(error));
    }
    return data ? mapPhase(data) : null;
};

/**
 * Legt die Planungsphase an — oder gibt die bestehende zurueck.
 *
 * Der Unique-Index auf `project_id` ist der Riegel: zwei gleichzeitig
 * geoeffnete Sitzungen wuerden sonst zwei Koepfe anlegen und jede Sitzung
 * saehe ihre eigenen Fragen. Bei `23505` wird deshalb nicht gemeldet,
 * sondern der vorhandene Satz gelesen — das ist kein Fehler, sondern das
 * erwartete Ergebnis des Wettlaufs.
 */
export const ensurePlanungsphase = async (projectId: string): Promise<Planungsphase> => {
    if (!projectId) throw new Error('Ohne Projekt kann keine Planungsphase angelegt werden.');
    const vorhanden = await fetchPlanungsphase(projectId);
    if (vorhanden) return vorhanden;

    const { data, error } = await supabase
        .from(T_PHASE)
        .insert({
            project_id: projectId,
            status: 'entwurf',
            katalog_version: aktuelleKatalogVersion(),
        })
        .select('*')
        .single();

    if (error) {
        if (istDublette(error)) {
            const jetzt = await fetchPlanungsphase(projectId);
            if (jetzt) return jetzt;
        }
        throw new Error(describeError(error));
    }
    return mapPhase(data);
};

export const updatePlanungsphase = async (
    id: string,
    updates: Partial<Planungsphase>,
): Promise<Planungsphase> => {
    const rein = ohneGesperrteSpalten(updates);
    if (!Object.keys(rein).length) throw new Error('Es gibt nichts zu speichern.');
    const { data, error } = await supabase
        .from(T_PHASE)
        .update(rein)
        .eq('id', id)
        .select('*')
        .single();
    if (error) throw new Error(describeError(error));
    return mapPhase(data);
};

/**
 * Abschliessen. Der Guard steht IM UPDATE (siehe (b)): eine bereits
 * abgeschlossene Planungsphase wird nicht ein zweites Mal gestempelt, sonst
 * verschoebe ein versehentlicher Doppelklick das Abschlussdatum.
 *
 * Ob abgeschlossen werden DARF, entscheidet `pruefeFreigabe` im Client —
 * das ist eine fachliche Frage ueber Fragen und Hinweise und gehoert nicht
 * in eine `where`-Bedingung.
 */
export const abschliessenPlanungsphase = async (
    id: string,
    jetztIso: string,
): Promise<boolean> => {
    const { data, error } = await supabase
        .from(T_PHASE)
        .update({ status: 'abgeschlossen' as PlanungStatus, abgeschlossen_am: jetztIso })
        .eq('id', id)
        .is('abgeschlossen_am', null)
        .select('id');
    if (error) throw new Error(describeError(error));
    return (data || []).length > 0;
};

export const wiedereroeffnenPlanungsphase = async (id: string): Promise<void> => {
    const { error } = await supabase
        .from(T_PHASE)
        .update({ status: 'laufend' as PlanungStatus, abgeschlossen_am: null })
        .eq('id', id);
    if (error) throw new Error(describeError(error));
};

export type RestOffenErgebnis =
    | { status: 'ok'; phase: Planungsphase }
    | { status: 'veraltet' }   // die Merkmale haben sich seit der Anzeige geaendert (Fingerabdruck passt nicht)
    | { status: 'konflikt' };  // zwischen Lesen und Schreiben hat jemand gespeichert (0 Zeilen)

/**
 * Vermerkt „Rest bleibt offen" (`merkmale.leitfadenRestOffen`) — als
 * Lese-Aendern-Schreiben mit optimistischer Sperre auf `updated_at`, wie
 * `aendereEmpfaenger`.
 *
 * Warum nicht das `merkmale` des geladenen Kopfsatzes zurueckschreiben: eine
 * zweite Sitzung, die inzwischen ein Merkmal gespeichert hat, verloere ihre
 * Aenderung still (Codex-Review P2). Deshalb wird die Zeile FRISCH gelesen
 * (`id`, `merkmale`, `updated_at`):
 *   - passt der Fingerabdruck der frischen Merkmale nicht mehr zu dem, den die
 *     Anzeige bestaetigt hat, wird NICHT geschrieben → `veraltet` (der
 *     Anwender hat einen anderen Stand gesehen, als jetzt gespeichert ist);
 *   - sonst Teil-Update NUR der Spalte `merkmale` = frische Merkmale plus
 *     Vermerk, mit der Bedingung `updated_at = <gelesen>` IM UPDATE (Hausregel
 *     (b) — entscheiden tut die Bedingung, das Lesen liefert nur den
 *     Vergleichswert). `planungsphasen_touch_trg` setzt `updated_at` bei jedem
 *     Update neu; trifft die Bedingung 0 Zeilen, hat jemand dazwischen
 *     gespeichert → `konflikt`, kein Neuversuch (der Anwender soll den neuen
 *     Stand sehen, bevor er erneut bestaetigt).
 * Der Zeitstempel geht als gelesener STRING zurueck (Mikrosekunden, siehe
 * `aendereEmpfaenger`). Fehler werden geworfen — Schreiben ist nicht fail-open.
 */
export const vermerkeRestOffen = async (phaseId: string, fingerabdruck: string): Promise<RestOffenErgebnis> => {
    if (!phaseId) throw new Error('Ohne Planungsphase lässt sich „Rest bleibt offen" nicht vermerken.');

    const { data: zeile, error: leseFehler } = await supabase
        .from(T_PHASE)
        .select('id, merkmale, updated_at')
        .eq('id', phaseId)
        .maybeSingle();
    if (leseFehler) throw new Error(describeError(leseFehler));
    const row = zeile as Record<string, unknown> | null;
    if (!row) throw new Error('Die Planungsphase wurde nicht gefunden — bitte neu laden.');
    const stand = row.updated_at;
    if (typeof stand !== 'string' || !stand) {
        throw new Error('Der Stand der Planungsphase (updated_at) ist nicht lesbar — Vermerk nicht gespeichert.');
    }

    const frisch = merkmaleAus(row.merkmale);
    if (merkmaleFingerabdruck(frisch) !== fingerabdruck) return { status: 'veraltet' };

    const { data, error } = await supabase
        .from(T_PHASE)
        .update({ merkmale: { ...frisch, leitfadenRestOffen: fingerabdruck } })
        .eq('id', phaseId)
        .eq('updated_at', stand)
        .select('*');
    if (error) throw new Error(describeError(error));
    const getroffen = (data || []) as Record<string, unknown>[];
    if (!getroffen.length) return { status: 'konflikt' };
    return { status: 'ok', phase: mapPhase(getroffen[0]) };
};

// ═══════════════════════════════════════════════════════════════════════════
// EMPFAENGER JE ADRESSAT — Spalte `empfaenger` (Migration Nr. 162)
// ═══════════════════════════════════════════════════════════════════════════

/** Der Hinweis, den die Oberflaeche zeigt, solange Nr. 162 nicht gefahren ist. */
export const EMPFAENGER_SPALTE_FEHLT =
    'Die Spalte für die Empfänger fehlt noch (Migration Nr. 162) — Zuordnungen können erst danach gespeichert werden.';

export interface EmpfaengerLesung {
    zuordnung: EmpfaengerZuordnung;
    /** Gesetzt, wenn die Spalte (oder die Tabelle) fehlt: die Zuordnung ist
     *  dann leer, und der Aufrufer soll das sagen statt zu schweigen. */
    hinweis: string | null;
}

/**
 * Die Zuordnung Adressat → Person einer Planungsphase. Liest NUR die Spalte
 * `empfaenger` — nicht den ganzen Kopfsatz, dessen Typ die Spalte bewusst
 * nicht traegt (`Planungsphase` in types.ts).
 *
 * FAIL-OPEN (Hausregel (a)): fehlt die Spalte (42703, Nr. 162 nicht
 * gefahren) oder die Tabelle (Nr. 39), kommt eine leere Zuordnung mit
 * gesetztem `hinweis` zurueck. Jeder andere Fehler wird geworfen.
 */
export const fetchEmpfaenger = async (planungId: string): Promise<EmpfaengerLesung> => {
    if (!planungId) return { zuordnung: {}, hinweis: null };
    const { data, error } = await supabase
        .from(T_PHASE)
        .select('empfaenger')
        .eq('id', planungId)
        .maybeSingle();
    if (error) {
        if (istFehlendeSpalte(error)) return { zuordnung: {}, hinweis: EMPFAENGER_SPALTE_FEHLT };
        if (istFehlendeTabelle(error)) {
            return { zuordnung: {}, hinweis: 'Die Tabellen der Planungsphase fehlen noch (Migration Nr. 39).' };
        }
        throw new Error(describeError(error));
    }
    const row = data as Record<string, unknown> | null;
    return { zuordnung: normalisiereEmpfaenger(row ? row.empfaenger : null), hinweis: null };
};

const MAX_EMPFAENGER_VERSUCHE = 3;

/**
 * Aendert EINEN Adressaten der Zuordnung — Lese-Aendern-Schreiben mit
 * optimistischer Sperre auf `updated_at`, als Teil-Update NUR der Spalte
 * `empfaenger`.
 *
 * Warum nicht die ganze Zuordnung schreiben: zwei Sitzungen, die denselben
 * Stand geladen haben, ueberschrieben sich gegenseitig — die zweite loeschte
 * still, was die erste zugeordnet hatte (Codex-Review P2). Deshalb wird je
 * Aenderung die Zeile FRISCH gelesen (`empfaenger`, `updated_at`), nur der
 * eine Adressat gesetzt oder entfernt und mit der Bedingung
 * `updated_at = <gelesen>` geschrieben. `planungsphasen_touch_trg` (Nr. 39,
 * BEFORE UPDATE) setzt `updated_at` bei JEDEM Update auf `now()`; hat jemand
 * dazwischen geschrieben, trifft die Bedingung 0 Zeilen, und der Lauf liest
 * neu und wendet die Aenderung auf den fremden Stand an — bis zu drei Mal,
 * dann Klartext. Der Zeitstempel geht als der gelesene STRING unveraendert
 * zurueck: PostgREST liefert Mikrosekunden, `new Date()` runde auf
 * Millisekunden und traefe nie.
 *
 * DERSELBE ADRESSAT von jemand anderem: trifft die Sperre nicht und steht
 * der Zieladressat beim Neulesen ANDERS da als beim ersten Lesen, wird NICHT
 * geschrieben — Ergebnis `konflikt` mit dem fremden Stand. Sonst setzte der
 * Neuversuch den eigenen, aelteren Eintrag ueber den fremden (Codex-Review
 * Runde 2). Andere Adressaten duerfen sich dazwischen aendern, sie werden
 * uebernommen. Steht der Zieladressat inzwischen schon so da, wie gewuenscht,
 * ist das `unveraendert`, kein Konflikt.
 *
 * Die Vergleichsbasis gehoert zum AUFTRAG, nicht zum Versuch: `vorgabeBasis`
 * kommt vom Schreiber, der sie nach einem gescheiterten Versuch aus dem
 * `EmpfaengerSchreibFehler` uebernimmt — ein spaeterer Anstoss (auch „Erneut
 * speichern") prueft also weiter gegen den Zieladressaten, wie er beim
 * ERSTEN Lesen war; sonst ueberschriebe er mit frisch gelesener Basis eine
 * fremde Aenderung ohne Konflikt (Codex-Review Runde 3). Sind die Versuche
 * ausgeschoepft, wird einmal nachgelesen: fremder Zieladressat → `konflikt`
 * (der Auftrag entfaellt), sonst Fehler MIT Basis (der Auftrag bleibt).
 * Jeder Fehler dieser Funktion ist ein `EmpfaengerSchreibFehler` und traegt
 * die Basis, sobald sie gelesen war.
 *
 * Hausregel (b) gilt hier im Kern weiter: ENTSCHEIDEN tut die Bedingung IM
 * UPDATE, das Lesen liefert nur den Vergleichswert. Keine andere Spalte im
 * Payload — ein gleichzeitiges „Grundlagen speichern" (das `empfaenger` nie
 * mitschickt, `GESPERRTE_SPALTEN`) wird nicht ueberschrieben. Es wird nur
 * geschrieben, wenn sich dadurch etwas aendert.
 *
 * `zuordnung` im Ergebnis ist immer, was die Datenbank danach traegt —
 * einschliesslich der Aenderungen anderer. Nicht fail-open: ohne Nr. 162
 * antwortet die Datenbank mit 42703 bzw. PostgREST mit PGRST204, beides wird
 * mit Klartext gemeldet.
 */
export const aendereEmpfaenger = async (
    planungId: string,
    adressat: FrageAdressat,
    eintrag: EmpfaengerEintrag | null,
    /** Vergleichsbasis aus einem frueheren Versuch DESSELBEN Auftrags (vom Schreiber). */
    vorgabeBasis: EmpfaengerBasis | null = null,
): Promise<EmpfaengerSchreibErgebnis> => {
    /** Der Zieladressat beim ERSTEN Lesen dieses Auftrags — Massstab dafuer,
     *  ob sich jemand anderes seither genau daran vergriffen hat. */
    let basis: EmpfaengerBasis | null = vorgabeBasis;
    const fehler = (text: string) => new EmpfaengerSchreibFehler(text, basis);
    if (!planungId) throw fehler('Ohne Planungsphase lässt sich kein Empfänger zuordnen.');

    /** Frisch lesen: Zuordnung und Stempel dieser Zeile. */
    const lies = async (): Promise<{ gelesen: EmpfaengerZuordnung; stand: string }> => {
        const { data: zeile, error: leseFehler } = await supabase
            .from(T_PHASE)
            .select('empfaenger, updated_at')
            .eq('id', planungId)
            .maybeSingle();
        if (leseFehler) {
            if (istFehlendeSpalte(leseFehler)) throw fehler(EMPFAENGER_SPALTE_FEHLT);
            throw fehler(describeError(leseFehler));
        }
        const row = zeile as Record<string, unknown> | null;
        if (!row) throw fehler('Die Planungsphase wurde nicht gefunden — bitte neu laden.');
        const stand = row.updated_at;
        if (typeof stand !== 'string' || !stand) {
            throw fehler('Der Stand der Planungsphase (updated_at) ist nicht lesbar — Zuordnung nicht gespeichert.');
        }
        return { gelesen: normalisiereEmpfaenger(row.empfaenger), stand };
    };

    for (let versuch = 0; versuch < MAX_EMPFAENGER_VERSUCHE; versuch++) {
        const { gelesen, stand } = await lies();
        const aktuell = gelesen[adressat] ?? null;
        const neu = setzeEmpfaenger(gelesen, adressat, eintrag);
        // Steht es schon so in der Datenbank, gibt es nichts zu schreiben.
        if (neu === gelesen) return { status: 'unveraendert', zuordnung: gelesen };
        // Gibt es eine Basis (frueherer Versuch dieses Auftrags oder Neuversuch
        // hier): hat sich der ZIELADRESSAT seither geaendert, war jemand
        // anderes genau hier schneller.
        if (basis && !gleicherEintrag(aktuell, basis.eintrag)) return { status: 'konflikt', zuordnung: gelesen };
        if (!basis) basis = { eintrag: aktuell };

        const { data, error } = await supabase
            .from(T_PHASE)
            .update({ empfaenger: neu })
            .eq('id', planungId)
            .eq('updated_at', stand)
            .select('empfaenger');
        if (error) {
            if (istFehlendeSpalte(error)) throw fehler(EMPFAENGER_SPALTE_FEHLT);
            throw fehler(describeError(error));
        }
        const getroffen = (data || []) as Record<string, unknown>[];
        if (getroffen.length) {
            return { status: 'geschrieben', zuordnung: normalisiereEmpfaenger(getroffen[0].empfaenger) };
        }
        // 0 Zeilen: jemand hat dazwischen geschrieben — neu lesen, erneut anwenden.
    }

    // Versuche ausgeschoepft: einmal nachlesen. Hat sich der Zieladressat
    // gegenueber der Basis geaendert, ist das ein Konflikt — der Auftrag
    // entfaellt. Sonst bleibt er mit seiner Basis im Schreiber und wird beim
    // naechsten Anstoss erneut versucht.
    const { gelesen } = await lies();
    const aktuell = gelesen[adressat] ?? null;
    if (setzeEmpfaenger(gelesen, adressat, eintrag) === gelesen) return { status: 'unveraendert', zuordnung: gelesen };
    if (basis && !gleicherEintrag(aktuell, basis.eintrag)) return { status: 'konflikt', zuordnung: gelesen };
    throw fehler(
        'Die Empfänger wurden gleichzeitig von jemand anderem geändert — bitte die Planungsphase neu laden und die Zuordnung erneut setzen.',
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// FRAGEN
// ═══════════════════════════════════════════════════════════════════════════

const SEITE = 1000;

/**
 * Alle Fragen einer Planungsphase.
 *
 * Gepaged, weil PostgREST bei 1000 Zeilen OHNE MELDUNG deckelt — der
 * Vollkatalog liegt bei ueber 200 Fragen, mehrere Bauabschnitte kommen dem
 * Limit aber nahe genug, dass die stille Kappung eintreten kann.
 */
export const fetchPlanungsfragen = async (planungId: string): Promise<Planungsfrage[]> => {
    if (!planungId) return [];
    const alle: Planungsfrage[] = [];
    for (let von = 0; ; von += SEITE) {
        const { data, error } = await supabase
            .from(T_FRAGE)
            .select('*')
            .eq('planung_id', planungId)
            .order('sortierung', { ascending: true })
            // Zweites Kriterium: `sortierung` allein ist nicht eindeutig, und
            // eine uneindeutige Sortierung laesst beim Blaettern Zeilen
            // doppelt erscheinen oder ganz ausfallen.
            .order('id', { ascending: true })
            .range(von, von + SEITE - 1);
        if (error) {
            if (istFehlendeTabelle(error)) return [];
            throw new Error(describeError(error));
        }
        const seite = data || [];
        alle.push(...seite.map(mapFrage));
        if (seite.length < SEITE) return alle;
    }
};

/**
 * Legt Fragen an. `upsert` mit `ignoreDuplicates` statt `insert`, damit ein
 * zweiter Klick auf "Katalog erzeugen" nicht mit 23505 abbricht, sondern
 * schlicht nichts zusaetzlich anlegt — der Unique-Index (planung_id, code)
 * bleibt der Riegel.
 *
 * In Bloecken zu 200, weil ein einzelnes Insert mit 211 Zeilen die
 * PostgREST-Nutzlast unnoetig gross macht und ein Fehler dann alles
 * mitnimmt.
 */
export const createPlanungsfragen = async (
    planungId: string,
    projectId: string,
    fragen: NeuePlanungsfrage[],
): Promise<Planungsfrage[]> => {
    if (!planungId || !projectId || !fragen || !fragen.length) return [];
    const BLOCK = 200;
    const erzeugt: Planungsfrage[] = [];
    for (let i = 0; i < fragen.length; i += BLOCK) {
        const zeilen = fragen.slice(i, i + BLOCK).map(f => ({
            ...f,
            planung_id: planungId,
            project_id: projectId,
        }));
        const { data, error } = await supabase
            .from(T_FRAGE)
            .upsert(zeilen, { onConflict: 'planung_id,code', ignoreDuplicates: true })
            .select('*');
        if (error) throw new Error(describeError(error));
        erzeugt.push(...(data || []).map(mapFrage));
    }
    return erzeugt;
};

/** Spalten der Antwortquelle (Migration Nr. 190). */
const QUELLEN_SPALTEN: readonly string[] = ['antwort_von', 'antwort_weg', 'antwort_beleg_id'];

/** Klartext, wenn NUR die Quelle geschrieben werden soll, die Spalten aber fehlen. */
export const QUELLE_SPALTE_FEHLT =
    'Die Quelle der Antwort (Antwort von, Weg, Beleg) lässt sich erst speichern, wenn die Datenbank-Migration '
    + 'Nr. 190 (supabase/planungsfragen_antwortquelle.sql) gelaufen ist.';

/**
 * Teil-Update einer Frage.
 *
 * Vor Migration Nr. 190 kennt die Tabelle die Quellenspalten nicht. Ruecklauf
 * und Antwortmail schicken sie zusammen mit dem Antworttext — damit die
 * Antwort dann nicht mit verloren geht, wird bei „Spalte fehlt" EINMAL ohne
 * die Quellenspalten geschrieben (die zurueckgelesene Zeile traegt die Quelle
 * dann nicht, die Oberflaeche zeigt also den echten Stand). Bestand die
 * Aenderung NUR aus der Quelle, gibt es den Klartext `QUELLE_SPALTE_FEHLT`.
 * Ein Speichern ganz ohne Quellenfelder ist davon nicht beruehrt.
 */
export const updatePlanungsfrage = async (
    id: string,
    updates: Partial<Planungsfrage>,
): Promise<Planungsfrage> => {
    const rein = ohneGesperrteSpalten(updates);
    if (!Object.keys(rein).length) throw new Error('Es gibt nichts zu speichern.');
    const schreibe = (felder: Record<string, unknown>) => supabase
        .from(T_FRAGE)
        .update(felder)
        .eq('id', id)
        .select('*')
        .single();
    const { data, error } = await schreibe(rein);
    if (!error) return mapFrage(data);
    if (istFehlendeSpalte(error) && QUELLEN_SPALTEN.some(k => k in rein)) {
        const ohneQuelle = Object.fromEntries(Object.entries(rein).filter(([k]) => !QUELLEN_SPALTEN.includes(k)));
        if (!Object.keys(ohneQuelle).length) throw new Error(QUELLE_SPALTE_FEHLT);
        const zweiter = await schreibe(ohneQuelle);
        if (zweiter.error) throw new Error(describeError(zweiter.error));
        return mapFrage(zweiter.data);
    }
    throw new Error(describeError(error));
};

/**
 * Markiert Fragen als gestellt und setzt die Frist.
 *
 * Der Guard `is('gestellt_am', null)` steht IM UPDATE: wird dasselbe Blatt
 * zweimal verschickt, bleibt das ERSTE Stelldatum stehen. Das ist Absicht —
 * fuer die Fristberechnung und im Haftungsfall zaehlt, wann zum ersten Mal
 * gefragt wurde, nicht wann zuletzt erinnert wurde.
 *
 * Gibt die Zahl der tatsaechlich gestempelten Zeilen zurueck.
 */
export const markiereGestellt = async (
    ids: string[],
    gestelltAmIso: string,
    fristAm: string | null,
): Promise<number> => {
    if (!ids || !ids.length) return 0;
    const { data, error } = await supabase
        .from(T_FRAGE)
        .update({ status: 'gestellt', gestellt_am: gestelltAmIso, frist_am: fristAm || null })
        .in('id', ids)
        .is('gestellt_am', null)
        .select('id');
    if (error) throw new Error(describeError(error));
    return (data || []).length;
};

/**
 * Stellt eine gemischte Auswahl: je Fristgruppe EIN `markiereGestellt` mit
 * der eigenen Frist (A-Fragen 14 Tage, B/C 21 — Aufteilung in
 * `fristGruppen.ts`). Dasselbe Stelldatum fuer alle Gruppen, denn gestellt
 * wird in einem Zug.
 *
 * Die Gruppen werden nacheinander geschrieben. Scheitert eine, wirft der
 * Aufruf — die davor liegenden sind dann schon gestempelt, und der Aufrufer
 * muss die Lage neu laden, damit die Oberflaeche nicht hinter der Datenbank
 * zurueckbleibt.
 */
export const markiereGestelltJeFrist = async (
    gruppen: Pick<FristGruppe, 'ids' | 'tage'>[],
    gestelltAmIso: string,
): Promise<FristErgebnis[]> => {
    const ergebnisse: FristErgebnis[] = [];
    // Frist ab dem KALENDERTAG des Stellens in Ortszeit. Der Zeitstempel ist
    // UTC — kurz nach Mitternacht waere sein Datumsteil der Vortag, und die
    // Frist endete einen Tag zu frueh.
    const gestellt = new Date(gestelltAmIso);
    const abTag = isFinite(gestellt.getTime()) ? heuteLokalIso(gestellt) : '';
    for (const g of gruppen || []) {
        if (!g || !g.ids || !g.ids.length) continue;
        const fristAm = fristDatum(abTag, g.tage);
        if (!fristAm) throw new Error('Die Frist konnte nicht berechnet werden — es wurde nichts als gestellt markiert.');
        const gestempelt = await markiereGestellt(g.ids, gestelltAmIso, fristAm);
        ergebnisse.push({ tage: g.tage, fristAm, gesamt: g.ids.length, gestempelt });
    }
    return ergebnisse;
};

/**
 * Haelt nach dem Versand fest, an WEN die Fragen gingen
 * (`empfaenger_name`/`empfaenger_email`) — Teil-Update nur dieser beiden
 * Spalten und nur dort, wo noch kein Name steht.
 *
 * Der Guard steht IM UPDATE (siehe (b)): `empfaenger_name` ist NULL oder
 * leer. Die Fragenliste schreibt beim Leeren des Feldes '' und nicht NULL,
 * deshalb beide Faelle. Traegt eine Frage schon einen Namen, bleibt er —
 * wie beim Stelldatum zaehlt der ERSTE Empfaenger: an ihn wurde zuerst
 * gefragt, und ein Erinnerungsschreiben an jemand anderen aendert daran
 * nichts. Die E-Mail wird nur zusammen mit dem Namen gesetzt; eine Frage mit
 * Name ohne E-Mail bekommt keine fremde Adresse untergeschoben.
 *
 * Gibt die Zahl der Zeilen zurueck, die jetzt den Empfaenger tragen.
 */
export const setzeFrageEmpfaengerWoLeer = async (
    ids: string[],
    name: string,
    email: string | null,
): Promise<number> => {
    if (!ids || !ids.length) return 0;
    const rein = (name || '').trim();
    if (!rein) throw new Error('Ohne Namen lässt sich kein Empfänger an den Fragen festhalten.');
    const { data, error } = await supabase
        .from(T_FRAGE)
        .update({ empfaenger_name: rein, empfaenger_email: (email || '').trim() || null })
        .in('id', ids)
        .or('empfaenger_name.is.null,empfaenger_name.eq.')
        .select('id');
    if (error) throw new Error(describeError(error));
    return (data || []).length;
};

/** Erneuert die Frist bereits gestellter Fragen, ohne das Stelldatum
 *  anzufassen (Erinnerung / Nachfassen). */
export const verlaengereFrist = async (ids: string[], fristAm: string): Promise<number> => {
    if (!ids || !ids.length || !fristAm) return 0;
    const { data, error } = await supabase
        .from(T_FRAGE)
        .update({ status: 'gestellt', frist_am: fristAm })
        .in('id', ids)
        .not('gestellt_am', 'is', null)
        .select('id');
    if (error) throw new Error(describeError(error));
    return (data || []).length;
};

/**
 * Setzt abgelaufene Fristen auf `nicht_beantwortet`. Die Auswahl trifft
 * `faelligeStatuswechsel` (pure, getestet), hier wird nur geschrieben.
 *
 * Beide Bedingungen stehen IM UPDATE (siehe (b)): nur Zeilen, die noch
 * `gestellt` sind UND deren Frist vor `heuteIso` liegt. Hat eine zweite
 * Sitzung inzwischen eine Antwort eingetragen oder die Frist verlaengert,
 * bleibt die Zeile unangetastet — der Koordinator stellt fest, was IST, und
 * nicht, was er beim Lesen gesehen hat. `frist_am` ist eine `date`-Spalte,
 * `heuteIso` muss deshalb YYYY-MM-DD sein; der Fristtag selbst zaehlt wie in
 * `istUeberfaellig` noch nicht als abgelaufen.
 */
export const markiereNichtBeantwortet = async (ids: string[], heuteIso: string): Promise<number> => {
    if (!ids || !ids.length) return 0;
    const heute = (heuteIso || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(heute)) {
        throw new Error('Ohne gültiges Tagesdatum lässt sich kein Fristablauf feststellen.');
    }
    const { data, error } = await supabase
        .from(T_FRAGE)
        .update({ status: 'nicht_beantwortet' })
        .in('id', ids)
        .eq('status', 'gestellt')
        .lt('frist_am', heute)
        .select('id');
    if (error) throw new Error(describeError(error));
    return (data || []).length;
};

/**
 * Sammel-Knopf „gegenstandslos": setzt Fragen, die nach den aktuellen
 * Merkmalen nicht mehr zutreffen, auf `gegenstandslos` und schreibt die
 * Begruendung in die interne Bewertung. Die Auswahl trifft
 * `nichtMehrZutreffend` (pure, getestet), hier wird nur geschrieben.
 *
 * Die Bedingungen stehen IM UPDATE (siehe (b)): nur Zeilen, die noch `offen`
 * sind, nie gestellt wurden, keine Antwort tragen und deren Bewertung noch
 * genau der Text ist, unter den die Begruendung gehaengt wurde (`bisher`).
 * Hat eine zweite Sitzung die Frage inzwischen versendet, beantwortet oder
 * bewertet, bleibt sie unangetastet — ihr Text wird nie ueberschrieben.
 *
 * Gleiche alte und neue Bewertung → ein Aufruf fuer alle; eine vorhandene
 * Bewertung macht die Zeile zum Einzelfall.
 *
 * Gibt die Zahl der tatsaechlich geaenderten Zeilen zurueck.
 */
export const markiereGegenstandslos = async (
    eintraege: { id: string; bisher: string; bewertung: string }[],
): Promise<number> => {
    const liste = (eintraege || []).filter(e => e && e.id && e.bewertung);
    if (!liste.length) return 0;
    const gruppen = new Map<string, { bisher: string; bewertung: string; ids: string[] }>();
    liste.forEach(e => {
        const bisher = e.bisher || '';
        const schluessel = JSON.stringify([bisher, e.bewertung]);
        const g = gruppen.get(schluessel) || { bisher, bewertung: e.bewertung, ids: [] };
        g.ids.push(e.id);
        gruppen.set(schluessel, g);
    });
    let geaendert = 0;
    for (const { bisher, bewertung, ids } of gruppen.values()) {
        const basis = supabase
            .from(T_FRAGE)
            .update({ status: 'gegenstandslos', bewertung })
            .in('id', ids)
            .eq('status', 'offen')
            .is('gestellt_am', null);
        // Leer heisst NULL oder '' — fuer Antwort UND Bewertung. Ein vorhandener
        // Bewertungstext geht ueber `eq` (Kodierung durch den Client), nie in
        // den `or`-Ausdruck.
        const gefiltert = bisher
            ? basis.eq('bewertung', bisher).or('antwort.is.null,antwort.eq.')
            : basis.or('and(antwort.is.null,bewertung.is.null),and(antwort.is.null,bewertung.eq.),'
                + 'and(antwort.eq.,bewertung.is.null),and(antwort.eq.,bewertung.eq.)');
        const { data, error } = await gefiltert.select('id');
        if (error) throw new Error(describeError(error));
        geaendert += (data || []).length;
    }
    return geaendert;
};

/**
 * Stempelt die Uebernahme einer Erkenntnis in das Zielmodul
 * (`uebernommen_am`). Wird NUR per Klick aus dem Zielmodul aufgerufen —
 * nichts gilt still als uebernommen.
 *
 * Der Guard `is('uebernommen_am', null)` steht IM UPDATE (siehe (b)), wie
 * bei `markiereGestellt`: die ERSTE Uebernahme bleibt stehen. Wer dieselbe
 * Antwort spaeter noch einmal in eine neue Fassung zieht, verschiebt damit
 * nicht den Zeitpunkt, zu dem sie zum ersten Mal weiterwirkte.
 *
 * Gibt die Zahl der tatsaechlich gestempelten Zeilen zurueck.
 */
export const markiereUebernommen = async (ids: string[], jetztIso: string): Promise<number> => {
    if (!ids || !ids.length) return 0;
    if (!jetztIso) throw new Error('Ohne Zeitpunkt lässt sich keine Übernahme stempeln.');
    const { data, error } = await supabase
        .from(T_FRAGE)
        .update({ uebernommen_am: jetztIso })
        .in('id', ids)
        .is('uebernommen_am', null)
        .select('id');
    if (error) throw new Error(describeError(error));
    return (data || []).length;
};

export const deletePlanungsfrage = async (id: string): Promise<void> => {
    const { error } = await supabase.from(T_FRAGE).delete().eq('id', id);
    if (error) throw new Error(describeError(error));
};

// ═══════════════════════════════════════════════════════════════════════════
// HINWEISE / BEDENKENANZEIGEN
// ═══════════════════════════════════════════════════════════════════════════

export const fetchPlanungshinweise = async (planungId: string): Promise<Planungshinweis[]> => {
    if (!planungId) return [];
    const { data, error } = await supabase
        .from(T_HINWEIS)
        .select('*')
        .eq('planung_id', planungId)
        .order('nummer', { ascending: true });
    if (error) {
        if (istFehlendeTabelle(error)) return [];
        throw new Error(describeError(error));
    }
    return (data || []).map(mapHinweis);
};

const MAX_NUMMER_VERSUCHE = 4;

/**
 * Legt einen Hinweis an und vergibt die laufende Nummer.
 *
 * Die Nummer wird IN der Schleife unmittelbar vor dem Insert bestimmt und bei
 * `23505` neu gelesen (siehe (c) im Dateikopf). Der Unique-Index
 * (project_id, nummer) ist der eigentliche Riegel — der Client raet nur gut.
 */
export const createPlanungshinweis = async (
    planungId: string,
    projectId: string,
    entwurf: NeuerPlanungshinweis,
): Promise<Planungshinweis> => {
    if (!planungId || !projectId) throw new Error('Ohne Projekt kann kein Hinweis angelegt werden.');

    let letzterFehler: unknown = null;
    for (let versuch = 0; versuch < MAX_NUMMER_VERSUCHE; versuch++) {
        const { data: bestand, error: leseFehler } = await supabase
            .from(T_HINWEIS)
            .select('nummer')
            .eq('project_id', projectId);
        if (leseFehler && !istFehlendeTabelle(leseFehler)) throw new Error(describeError(leseFehler));

        const nummer = naechsteHinweisNummer(
            ((bestand || []) as { nummer: number }[]).map(z => ({ nummer: z.nummer } as Planungshinweis)),
        );

        const { data, error } = await supabase
            .from(T_HINWEIS)
            .insert({ ...entwurf, planung_id: planungId, project_id: projectId, nummer })
            .select('*')
            .single();

        if (!error) return mapHinweis(data);
        if (!istDublette(error)) throw new Error(describeError(error));
        letzterFehler = error;
    }
    throw new Error(
        `Die laufende Nummer konnte nach ${MAX_NUMMER_VERSUCHE} Versuchen nicht vergeben werden. ${describeError(letzterFehler)}`,
    );
};

export const updatePlanungshinweis = async (
    id: string,
    updates: Partial<Planungshinweis>,
): Promise<Planungshinweis> => {
    const rein = ohneGesperrteSpalten(updates);
    if (!Object.keys(rein).length) throw new Error('Es gibt nichts zu speichern.');
    const { data, error } = await supabase
        .from(T_HINWEIS)
        .update(rein)
        .eq('id', id)
        .select('*')
        .single();
    if (error) throw new Error(describeError(error));
    return mapHinweis(data);
};

/**
 * Stempelt den Versand. Guard im UPDATE: eine Anzeige gilt als EINMAL
 * versendet. Der Zugangsnachweis haengt am ersten Versand — ein zweiter
 * Stempel wuerde im Streitfall das falsche Datum belegen.
 *
 * Gibt `false` zurueck, wenn bereits versendet war.
 */
export const markiereHinweisVersendet = async (
    id: string,
    versendetAmIso: string,
): Promise<boolean> => {
    const { data, error } = await supabase
        .from(T_HINWEIS)
        .update({ versendet_am: versendetAmIso })
        .eq('id', id)
        .is('versendet_am', null)
        .select('id');
    if (error) throw new Error(describeError(error));
    return (data || []).length > 0;
};

export const deletePlanungshinweis = async (id: string): Promise<void> => {
    const { error } = await supabase.from(T_HINWEIS).delete().eq('id', id);
    if (error) throw new Error(describeError(error));
};

// ═══════════════════════════════════════════════════════════════════════════
// GESAMTLAGE
// ═══════════════════════════════════════════════════════════════════════════

export interface PlanungsLage {
    phase: Planungsphase | null;
    fragen: Planungsfrage[];
    hinweise: Planungshinweis[];
}

/**
 * Alles auf einmal. Legt NICHTS an — ein blosses Oeffnen der Kachel soll
 * keinen Datensatz erzeugen; das ist dieselbe Ueberlegung wie beim
 * Schreibschutz fuer Nur-Ansicht-Projekte, wo Selbstheilungs-Migrationen
 * beim reinen Lesen geschrieben hatten.
 */
export const fetchPlanungsLage = async (
    projectId: string,
    optionen: PlanungLeseOptionen = {},
): Promise<PlanungsLage> => {
    const phase = await fetchPlanungsphase(projectId, optionen);
    if (!phase) return { phase: null, fragen: [], hinweise: [] };
    const [fragen, hinweise] = await Promise.all([
        fetchPlanungsfragen(phase.id),
        fetchPlanungshinweise(phase.id),
    ]);
    return { phase, fragen, hinweise };
};

// ═══════════════════════════════════════════════════════════════════════════
// PFLICHTENSTAND — Lese-Schicht fuer Hub, Ampel und Folgeunterlagen
// ═══════════════════════════════════════════════════════════════════════════

export interface PflichtenStandErgebnis {
    eintraege: PflichtEintrag[];
    /** Gesetzt, wenn nicht gelesen werden konnte — dann sind alle Eintraege
     *  `ungeklaert` und der Aufrufer soll das sagen statt zu schweigen. */
    lesefehler: string | null;
}

/**
 * Stand der fuenf Pflichten eines Projekts, live aus den Merkmalen
 * (`pflichtenStand`). Liest nur die Spalte `merkmale` — der Schnappschuss
 * `pruefung` wird hier bewusst nicht gelesen, er kann veraltet sein.
 *
 * FAIL-OPEN, wirft nie: fehlt die Tabelle oder schlaegt das Lesen fehl,
 * kommen alle fuenf als `ungeklaert` mit gesetztem `lesefehler` zurueck —
 * niemals `keine`. Ein Hub, der wegen eines Lesefehlers „nicht erforderlich"
 * zeigt, waere eine Entwarnung ohne Grundlage.
 */
export const fetchPflichtenStand = async (projectId: string): Promise<PflichtenStandErgebnis> => {
    const jetzt = new Date().toISOString();
    if (!projectId) return { eintraege: pflichtenStand(null, jetzt), lesefehler: null };
    const nichtLesbar = (grund: string): PflichtenStandErgebnis => ({
        eintraege: pflichtenUngeklaert('Planungsphase konnte nicht gelesen werden — Stand unbekannt.'),
        lesefehler: grund,
    });
    try {
        const { data, error } = await supabase
            .from(T_PHASE)
            .select('merkmale')
            .eq('project_id', projectId)
            .maybeSingle();
        if (error) {
            return nichtLesbar(istFehlendeTabelle(error)
                ? 'Die Tabelle der Planungsphase fehlt noch (Migration Nr. 39).'
                : describeError(error));
        }
        const row = data as Record<string, unknown> | null;
        const phase = row ? { merkmale: merkmaleAus(row.merkmale) } : null;
        return { eintraege: pflichtenStand(phase, jetzt), lesefehler: null };
    } catch (e) {
        return nichtLesbar(describeError(e));
    }
};
